import { Observable, of } from 'rxjs';
import { ComponentDataSourceService } from '../component-data-source.service';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { map, catchError } from 'rxjs/operators';
import { LoadDataParams } from '../../../interfaces/DataSource/DataSourceParams.interface';
import { UserManagementDTO } from '../../../dto/users/user-management.dto';
import { AuthService } from '../../auth/auth.service';
import { FacetFilterConfig, mapFacetsToTableFilters } from '../../../interfaces/DataSource/FacetFilterConfig.interface';

export const DUNA_USERS_FACET_FILTERS: FacetFilterConfig = {
  fields: [
    { key: 'is_active', label: 'Status', labelMap: { '1': 'Aktiv', '0': 'Inaktiv' } },
    { key: 'has_conflicts', label: 'Konflikter', labelMap: { '1': 'Ja', '0': 'Nej' } },
  ],
};

/** Maps table column keys to /users/manage sortBy fields. */
const USER_MANAGE_SORT_COLUMNS: Record<string, string> = {
  name: 'first_name',
  user: 'email',
};

function mapUserManageSortColumn(column: string): string {
  return USER_MANAGE_SORT_COLUMNS[column] ?? column;
}

function normalizeLoadParams(params?: LoadDataParams): LoadDataParams | undefined {
  if (!params) {
    return params;
  }

  let next: LoadDataParams = params;

  if (params.filters?.length) {
    next = {
      ...next,
      filters: params.filters.map((filter) => {
        if (filter.column === 'pageIndex' || filter.column === 'pageSize') {
          const n = Number(filter.value);
          return Number.isFinite(n) ? { ...filter, value: n } : filter;
        }
        return filter;
      }),
    };
  }

  if (params.sort?.active) {
    const active = mapUserManageSortColumn(params.sort.active);
    if (active !== params.sort.active) {
      next = {
        ...next,
        sort: { ...params.sort, active },
      };
    }
  }

  return next;
}

function paginationFromParams(params?: LoadDataParams): { pageIndex: number; pageSize: number } {
  const pageSizeRaw = params?.filters?.find((f) => f.column === 'pageSize')?.value;
  const pageIndexRaw = params?.filters?.find((f) => f.column === 'pageIndex')?.value;
  const pageSize = Math.max(1, Number(pageSizeRaw ?? 10) || 10);
  const pageIndex = Math.max(0, Number(pageIndexRaw ?? 0) || 0);
  return { pageIndex, pageSize };
}

function mapUserManageListResult(
  result: any,
  params?: LoadDataParams
): {
  items: UserManagementDTO[];
  totalCount: number;
  pageSize: number;
  pageIndex: number;
  facets?: unknown;
  filters?: unknown;
  settings?: unknown;
  error: string | null;
} {
  const fallback = paginationFromParams(params);

  if (result?.data && result?.meta) {
    return {
      items: result.data as UserManagementDTO[],
      totalCount: result.meta.totalItems ?? 0,
      pageSize: result.meta.pageSize ?? fallback.pageSize,
      pageIndex:
        result.meta.page != null ? Math.max(0, Number(result.meta.page) - 1) : fallback.pageIndex,
      facets: result.facets,
      filters: result.filters,
      settings: result.settings,
      error: result.error ?? null,
    };
  }

  return {
    items: (result?.items ?? []) as UserManagementDTO[],
    totalCount: result?.totalCount ?? result?.meta?.totalItems ?? 0,
    pageSize: result?.pageSize ?? result?.meta?.pageSize ?? fallback.pageSize,
    pageIndex:
      result?.pageIndex ??
      (result?.meta?.page != null
        ? Math.max(0, Number(result.meta.page) - 1)
        : fallback.pageIndex),
    facets: result?.facets,
    filters: result?.filters,
    settings: result?.settings,
    error: result?.error ?? null,
  };
}

export function duna_users(
  componentDataSourceService: ComponentDataSourceService,
  http: HttpClient,
  router: Router,
  authService: AuthService
) {
    return {
      name: 'DUNA Brugere',
      description: 'Administrer brugere og deres emails',
      execute: (params?: LoadDataParams) => {
        if (!authService.hasAnyRoleSync(['superadmin', 'orgadmin'])) {
          return of({
            items: [],
            totalCount: 0,
            pageSize: 10,
            pageIndex: 0,
            error: 'Kun superadmin og orgadmin har adgang til brugeradministration.',
          });
        }

        const requestParams = normalizeLoadParams(params);

        return componentDataSourceService['loadDataWithStandardFiltersGET'](
          'duna_users',
          '/users/manage',
          requestParams
        ).pipe(
          catchError((error) => {
            console.error('Error fetching users:', error);
            const message =
              error?.error?.message ||
              error?.error ||
              error?.message ||
              'Kunne ikke hente brugere';
            const { pageSize, pageIndex } = paginationFromParams(requestParams);
            return of({
              items: [],
              totalCount: 0,
              pageSize,
              pageIndex,
              error: typeof message === 'string' ? message : 'Kunne ikke hente brugere',
            });
          }),
          map((result) => mapUserManageListResult(result, requestParams))
        );
      },
      settings: {
        columns: [
          { 
            key: 'name', 
            header: 'Navn', 
            type: 'text',
            transform: (value: any, row?: UserManagementDTO) => {
              return `${row?.first_name || ''} ${row?.last_name || ''}`.trim() || 'N/A';
            },
            sortable: true
          },
          {
            key: 'organization_name',
            header: 'Organisation',
            type: 'text',
            transform: (value: string | null) => value || '—',
            sortable: true,
          },
          { 
            key: 'user', 
            header: 'Primær Email', 
            type: 'text',
            transform: (value: string | null) => value || 'Ingen email',
            sortable: true
          },
          {
            key: 'is_active',
            header: 'Status',
            type: 'component',
            componentName: 'badge',
            componentInputs: (row: UserManagementDTO) => {
              const isActive = row.is_active !== undefined && row.is_active !== null ? row.is_active : true;
              return {
                text: isActive ? 'Aktiv' : 'Inaktiv',
                color: isActive ? 'success' : 'error'
              };
            },
            sortable: true,
            alignment: 'center'
          },
          {
            key: 'secondary_emails',
            header: 'Sekundære Emails',
            type: 'text',
            transform: (value: any[], row?: UserManagementDTO) => {
              const emails = row?.secondary_emails || [];
              return emails.length > 0 ? `${emails.length} email${emails.length !== 1 ? 's' : ''}` : 'Ingen';
            },
            sortable: false
          },
          {
            key: 'conflict_count',
            header: 'Konflikter',
            type: 'component',
            componentName: 'icon',
            componentInputs: (row: UserManagementDTO) => {
              const count = row.conflict_count !== undefined && row.conflict_count !== null ? row.conflict_count : 0;
              return {
                icon: count > 0 ? 'warning' : null,
                color: count > 0 ? 'warn' : null,
                tooltip: count > 0 ? `${count} pending email konflikt${count !== 1 ? 'er' : ''}` : null
              };
            },
            sortable: true,
            alignment: 'center'
          },
          {
            key: 'access_groups',
            header: 'Adgangsgrupper',
            type: 'text',
            transform: (_value: unknown, row?: UserManagementDTO) => {
              const groups = row?.access_groups ?? [];
              if (!groups.length) {
                return 'Ingen';
              }
              return groups.map((g) => g.display_name).join(', ');
            },
            sortable: false,
          },
          {
            key: 'has_protected_role',
            header: 'Skrivebeskyttet',
            type: 'component',
            componentName: 'badge',
            componentInputs: (row: UserManagementDTO) => {
              const hasProtected = row.has_protected_role !== undefined && row.has_protected_role !== null ? row.has_protected_role : false;
              const protectedRoles = row.protected_roles && row.protected_roles.length > 0 ? row.protected_roles : [];
              
              const roleNames = protectedRoles.map(role => {
                return role.charAt(0).toUpperCase() + role.slice(1);
              }).join(', ');
              
              const displayText = hasProtected 
                ? (roleNames ? `Ja (${roleNames})` : 'Ja')
                : 'Nej';
              
              const tooltipText = hasProtected 
                ? `Denne bruger har ${protectedRoles.length > 1 ? 'beskyttede roller' : 'en beskyttet rolle'}: ${roleNames || 'orgbruger, orgadmin, eller superadmin'}. Brugeren kan ikke redigeres.`
                : null;
              
              return {
                text: displayText,
                color: hasProtected ? 'warn' : 'default',
                tooltip: tooltipText
              };
            },
            sortable: false,
            alignment: 'center'
          },
          {
            key: 'actions',
            header: '',
            type: 'button',
            buttonText: 'Administrer',
            buttonColor: 'primary',
            alignment: 'right',
            variant: 'raised',
            action: (row: UserManagementDTO) => {
              window.dispatchEvent(new CustomEvent('duna-users-row-click', { detail: row }));
            }
          }
        ],
        pageable: true,
        pageSize: 10,
        pageSizeOptions: [10, 25, 50, 100],
        sortable: true,
        rowHover: true,
        searchable: true,
        defaultSortColumn: 'first_name',
        defaultSortDirection: 'asc',
        rowClickable: true,
        facetFilters: DUNA_USERS_FACET_FILTERS,
        filters: mapFacetsToTableFilters({}, DUNA_USERS_FACET_FILTERS),
        onRowClick: (row: UserManagementDTO) => {
          // Handled by parent component via custom event
        }
      }
    }
}
